/**
 * FEATURE FLAGS CONTEXT
 * ─────────────────────────────────────────────────────────────
 * Admin can enable/disable features from the Admin Dashboard.
 * When a feature is disabled, it's hidden from the UI entirely.
 *
 * In production: replace persistence with API call to
 *   GET /api/admin/feature-flags
 *   PUT /api/admin/feature-flags/:flag
 */
import React, { createContext, useContext, useState, useCallback } from 'react';

// ── Feature definitions ──────────────────────────────────────
export const FEATURE_DEFINITIONS = [
  {
    key: 'certifications',
    label: 'Certifications',
    description: 'Project-based certification tracks with mentor review and certificate issuance.',
    icon: '🏆',
    category: 'Core',
    affectsRoutes: ['/certifications'],
    affectsNav: true,
    defaultEnabled: true,
  },
  {
    key: 'mentorEvents',
    label: 'Mentor Events',
    description: 'Meetup-style events created by mentors, visible on Home Screen with registration.',
    icon: '📅',
    category: 'Community',
    affectsRoutes: ['/events'],
    affectsNav: false,
    defaultEnabled: true,
  },
  {
    key: 'ratings',
    label: 'Ratings & Reviews',
    description: 'Star ratings and written reviews after completed sessions.',
    icon: '⭐',
    category: 'Trust',
    affectsRoutes: [],
    affectsNav: false,
    defaultEnabled: true,
  },
  {
    key: 'githubSubmission',
    label: 'GitHub Submission',
    description: 'GitHub link submission for certification project review.',
    icon: '🐙',
    category: 'Certifications',
    affectsRoutes: [],
    affectsNav: false,
    defaultEnabled: true,
  },
  {
    key: 'languageSelector',
    label: 'Language Selector',
    description: 'Multi-language support toggle in the Navbar.',
    icon: '🌐',
    category: 'UI',
    affectsRoutes: [],
    affectsNav: false,
    defaultEnabled: false, // Off until more languages are translated
  },
  {
    key: 'groupSessions',
    label: 'Group Sessions',
    description: 'Cohort-based group mentorship sessions.',
    icon: '👥',
    category: 'Sessions',
    affectsRoutes: [],
    affectsNav: false,
    defaultEnabled: false,
  },
  {
    key: 'aiMatching',
    label: 'AI Mentor Matching',
    description: 'AI-powered mentor recommendation based on learner goals and history.',
    icon: '🤖',
    category: 'AI',
    affectsRoutes: [],
    affectsNav: false,
    defaultEnabled: false,
  },
  {
    key: 'walletPayouts',
    label: 'Mentor Wallet & Payouts',
    description: 'Mentor wallet dashboard, payout request, and transaction history.',
    icon: '💰',
    category: 'Payments',
    affectsRoutes: [],
    affectsNav: false,
    defaultEnabled: true,
  },
  {
    key: 'supportTickets',
    label: 'Support Tickets',
    description: 'User support ticket creation and admin ticket management.',
    icon: '🎫',
    category: 'Support',
    affectsRoutes: [],
    affectsNav: false,
    defaultEnabled: true,
  },
  {
    key: 'mobileRelease',
    label: 'Mobile App Release Banner',
    description: 'Show Apple App Store and Google Play Store download buttons across the site. Toggle off to hide the mobile release section until the app is live.',
    icon: '📱',
    category: 'Marketing',
    affectsRoutes: [],
    affectsNav: false,
    defaultEnabled: false,
  },
  {
    key: 'messaging',
    label: 'In-App Messaging',
    description: 'Direct messaging between mentor and mentee.',
    icon: '💬',
    category: 'Communication',
    affectsRoutes: [],
    affectsNav: false,
    defaultEnabled: true,
  },
];

const getDefaults = () =>
  FEATURE_DEFINITIONS.reduce((acc, f) => {
    acc[f.key] = f.defaultEnabled;
    return acc;
  }, {});

const load = () => {
  try {
    const stored = localStorage.getItem('db_feature_flags');
    return stored ? { ...getDefaults(), ...JSON.parse(stored) } : getDefaults();
  } catch { return getDefaults(); }
};

const FeatureFlagContext = createContext(null);

export function FeatureFlagProvider({ children }) {
  const [flags, setFlags] = useState(load);

  const isEnabled = useCallback((key) => flags[key] ?? false, [flags]);

  const setFlag = useCallback((key, value) => {
    setFlags(prev => {
      const next = { ...prev, [key]: value };
      try { localStorage.setItem('db_feature_flags', JSON.stringify(next)); } catch {}
      return next;
    });
  }, []);

  const toggleFlag = useCallback((key) => {
    setFlags(prev => {
      const next = { ...prev, [key]: !prev[key] };
      try { localStorage.setItem('db_feature_flags', JSON.stringify(next)); } catch {}
      return next;
    });
  }, []);

  const resetAll = useCallback(() => {
    const defaults = getDefaults();
    setFlags(defaults);
    try { localStorage.setItem('db_feature_flags', JSON.stringify(defaults)); } catch {}
  }, []);

  return (
    <FeatureFlagContext.Provider value={{ flags, isEnabled, setFlag, toggleFlag, resetAll, definitions: FEATURE_DEFINITIONS }}>
      {children}
    </FeatureFlagContext.Provider>
  );
}

export function useFeatureFlags() {
  const ctx = useContext(FeatureFlagContext);
  if (!ctx) throw new Error('useFeatureFlags must be used inside FeatureFlagProvider');
  return ctx;
}
